// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getProp, resolveColor } from "@insite/mobius/utilities";
import color from "@steiner/XDL/Utilities/color";
import { css } from "styled-components";

/**
 * Add custom style for scrollbar
 * @param width
 */
export const styledScrollbar = (width = 4) => {
    // Define color variables for better readability
    const backgroundColor = "common.ui2";
    const thumbColor = "common.ui4";
    const thumbHoverColor = "common.ui3";

    return ({ theme }: { theme: any }) => css`
        overflow-y: auto;
        transition: scrollbar-color ${getProp("theme.transition.duration.regular")}ms ease-in-out;

        // Fallback for browsers that do not support ::-webkit-scrollbar
        @supports not (selector(::-webkit-scrollbar)) {
            scrollbar-width: thin;
            scrollbar-color: ${resolveColor(thumbColor, theme)} ${resolveColor(backgroundColor, theme)};
        }

        // WebKit-specific scrollbar styling
        &::-webkit-scrollbar {
            width: ${width}px;
            height: ${width}px;
        }
        &::-webkit-scrollbar-track {
            background: ${resolveColor(backgroundColor, theme)};
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${resolveColor(thumbColor, theme)};
            border-radius: 2px;
            border: 3px solid transparent; // Transparent border for spacing
        }
        &::-webkit-scrollbar-thumb:hover {
            background: ${resolveColor(thumbHoverColor, theme)};
        }
    `;
};

export const hideScrollBar = css`
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    /* this prevent scroll outside the container */
    overscroll-behavior-y: contain;
`;

export const myAccountTableScrollbar = css`
    overflow: auto;

    /* Customize scrollbar for webkit browsers */
    &::-webkit-scrollbar {
        height: 12px;
        width: 12px;
    }

    &::-webkit-scrollbar-track {
        background: ${color("common.ui2")};
        border-radius: 6px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${color("common.ui4")};
        border-radius: 6px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: ${color("common.ui5")};
    }

    /* For Firefox */
    scrollbar-width: thick;
    scrollbar-color: ${color("common.ui4")} ${color("common.ui2")};
`;

const hasVScroll = () => document.body.scrollHeight > document.body.clientHeight;

export const addScrollbarGutter = () => {
    if (typeof document === "undefined") {
        return;
    }

    if (!hasVScroll()) {
        return;
    }

    document.querySelector("html")?.setAttribute("style", "scrollbar-gutter: stable;");
};

export const removeScrollbarGutter = () => {
    if (typeof document === "undefined") {
        return;
    }

    if (!hasVScroll()) {
        return;
    }

    document.querySelector("html")?.removeAttribute("style");
};

/**
 * Scroll to invalid field
 * @param scrollToTop
 */
export const doScroll = (scrollToTop = false) => {
    setTimeout(
        () => {
            const selector = document.querySelector("[aria-invalid='true']");

            if (selector) {
                selector.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });

                return;
            }

            if (scrollToTop) {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "smooth",
                });
                return;
            }

            doScroll(scrollToTop);
        },
        scrollToTop ? 10 : 500,
    );
};
